import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Card, CardActionArea, CardActions, CardContent, Container, Grid, IconButton, TextField, Typography } from "@mui/material";
import { AppImageDimension, AppImages } from "app/appImages";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store, useAppDispatch } from "app/store";
import { MprcContainer, MprcIconButtonDropdown, MprcRequestErrorReport, MprcRequestIndicator, MprcText } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterBox, MprcFilterCount, MprcFilterLine } from "components/filter";
import { IconFilter, IconSort } from "components/icons";
import { KnowledgeSubscriptionType, ManagedQuickAccessFilterBy, ManagedQuickAccessOrderBy, ManagedQuickAccessResponse } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { CardThumbnail } from "views/general/Cards/Common";

////////////////////////////////////////////////////////////////////////////

interface IViewDataFilter {
    searchText: string;
    filterBy: ManagedQuickAccessFilterBy;
    orderBy: ManagedQuickAccessOrderBy;
}

////////////////////////////////////////////////////////////////////////////

function getDefaultViewFilter(): IViewDataFilter {
    return {
        orderBy: ManagedQuickAccessOrderBy.Name,
        filterBy: ManagedQuickAccessFilterBy.All,
        searchText: ""
    };
}

////////////////////////////////////////////////////////////////////////////

interface RenderCardProps {
    item: ManagedQuickAccessResponse;
    onToggleHide: (id: string) => void;
}
const RenderCard = (props: RenderCardProps) => {

    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();

    const { item: { hide, name, subscriptionId, subscriptionType, subscriptionName, thumbnailUrl }, onToggleHide } = props;

    const thumbUrl = thumbnailUrl
        ?? (subscriptionType == KnowledgeSubscriptionType.Channel
            ? AppImages.KnowledgeChannelThumbnail
            : subscriptionType == KnowledgeSubscriptionType.Topic
                ? AppImages.TopicThumbnail
                : subscriptionType == KnowledgeSubscriptionType.Author
                    ? AppImages.UserThumbnail
                    : subscriptionType == KnowledgeSubscriptionType.List
                        ? AppImages.PublicationTypeListThumbnail
                        : subscriptionType == KnowledgeSubscriptionType.Question
                            ? AppImages.PublicationTypeQuestionThumbnail
                            : undefined);

    const thumbLink = (subscriptionType == KnowledgeSubscriptionType.Channel
        ? KnowledgePaths.FeedChannel(subscriptionId)
        : subscriptionType == KnowledgeSubscriptionType.Topic
            ? KnowledgePaths.FeedTopic(subscriptionId)
            : subscriptionType == KnowledgeSubscriptionType.Author
                ? KnowledgePaths.FeedUserProfile(subscriptionId)
                : subscriptionType == KnowledgeSubscriptionType.List
                    ? KnowledgePaths.FeedList(subscriptionId)
                    : "#");

    return (
        <Card sx={{ width: AppImageDimension.ThumbnailWide.width }} elevation={2}>
            <CardActionArea onClick={() => navigate(thumbLink)}>
                <CardThumbnail
                    thumbnailUrl={thumbUrl ?? AppImages.TopicThumbnail}
                />
                <CardContent sx={{ p: 1, height: 36 }}>
                    <div>
                        <Typography variant="body1" fontWeight="bold" noWrap component="p">
                            {name}
                        </Typography>
                    </div>
                    <Typography variant="captionHeader" noWrap component="p">
                        {subscriptionName}
                    </Typography>
                </CardContent>
            </CardActionArea>
            <CardActions disableSpacing sx={{ p: 0 }}>
                <IconButton onClick={() => onToggleHide(subscriptionId)} sx={{ margin: "0px 0px 0px auto" }}>
                    <MprcText color={hide ? "warning" : "inherit"}>
                        <FontAwesomeIcon
                            icon={hide ? faEyeSlash : faEye}
                            title={hide ? t("QuickAccess.Edit.SubscriptionHideTooltip") : t("QuickAccess.Edit.SubscriptionVisibleTooltip")}
                        />
                    </MprcText>
                </IconButton>
            </CardActions>
        </Card>
    );
};

export const QuickAccessEdit: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [viewData, setViewData] = useState<ManagedQuickAccessResponse[] | null>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [toggleId, setToogleId] = useState<string | null>(null);
    const [filtered, setFiltered] = useState<boolean>(false);
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>(getDefaultViewFilter());
    const changed = useRef<boolean>(false);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleReload = useCallback(() => {
        setLoading(true);
    }, []);

    const handleOrderBy = useCallback((orderBy: ManagedQuickAccessOrderBy) => {
        setViewFilter(state => ({ ...state, orderBy }));
        handleReload();
    }, []);

    const handleFilterBy = useCallback((filterBy: ManagedQuickAccessFilterBy) => {
        setViewFilter(state => ({ ...state, filterBy }));
        handleReload();
    }, []);


    const handleSearchText = useCallback((searchText: string, reload: boolean) => {
        setViewFilter(state => ({ ...state, searchText }));
        if (reload) handleReload();
    }, []);


    ///
    const resetFilters = () => {
        setViewFilter((state) => ({ ...state, searchText: "", filterBy: ManagedQuickAccessFilterBy.All }));
        handleReload();
    };

    ///
    const handleAdd = () => {
        navigate(KnowledgePaths.Subcriptions);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function toggleHide() {
            try {
                const result = await api.v1.knowledgeSubcription.toggleHide({ subscriptionId: toggleId! });
                if (result.data === true) {
                    setLoading(true);
                    if (viewData) {
                        var index = viewData.findIndex(p => p.subscriptionId == toggleId!);
                        if (index > -1) {
                            LibUtils.arrayReplaceAt(viewData, index, { ...viewData[index], hide: !viewData[index].hide });
                            changed.current = true;
                            store.dispatch(fetchKnowledgeQuickAccess({}));
                        }
                    }
                }
                else enqueueSnackbar(t("QuickAccess.Edit.ToggleErrorMessage"), { variant: "error" });

            } catch {
                enqueueSnackbar(t("QuickAccess.Edit.ToggleErrorMessage"), { variant: "error" });
            }

            setToogleId(null);
        }
        if (toggleId != null) toggleHide();
    }, [toggleId]);


    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.knowledgeSubcription.managedQuickAccessList({
                    searchText: viewFilter.searchText,
                    filterBy: viewFilter.filterBy,
                    orderBy: viewFilter.orderBy,
                });

                setViewData(result.data);
                setFiltered(viewFilter.searchText.length > 0 || viewFilter.filterBy !== ManagedQuickAccessFilterBy.All);

            } catch (e) {
                setViewData(null);
                setViewError(e);
            }

            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);

    /** Recarrega QuickAccessList se algo mudou */
    useEffect(() => {
        return () => {
            if (changed.current === true) dispatch(fetchKnowledgeQuickAccess({}));
        };
    }, []);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const RenderContent = (
        <>
            <MprcContainer>
                <MprcFilterBox>
                    <MprcFilterLine>
                        <Grid container justifyContent="space-between" alignItems="center" width="100%">

                            <Grid size="grow">
                                <ButtonAdd size="large" color="inherit" onClick={handleAdd} text={t("QuickAccess.Edit.NewSubscriptionButton")} />
                            </Grid>

                            <Grid>

                                <MprcIconButtonDropdown
                                    menuItemCheck
                                    icon={<IconFilter />}
                                    selected={viewFilter.filterBy}
                                    onChange={handleFilterBy}
                                    items={[
                                        { text: t("QuickAccess.Edit.FilterBy.All"), value: ManagedQuickAccessFilterBy.All },
                                        { text: t("QuickAccess.Edit.FilterBy.Channel"), value: ManagedQuickAccessFilterBy.Channel },
                                        { text: t("QuickAccess.Edit.FilterBy.Topic"), value: ManagedQuickAccessFilterBy.Topic },
                                        { text: t("QuickAccess.Edit.FilterBy.Author"), value: ManagedQuickAccessFilterBy.Author },
                                        { text: t("QuickAccess.Edit.FilterBy.List"), value: ManagedQuickAccessFilterBy.List },
                                    ]}
                                    title={t("QuickAccess.Edit.FilterBy.ButtonTooltip")}
                                />

                                <MprcIconButtonDropdown
                                    menuItemCheck
                                    icon={<IconSort />}
                                    selected={viewFilter.orderBy}
                                    onChange={handleOrderBy}
                                    items={[
                                        { text: t("QuickAccess.Edit.OrderBy.Name"), value: ManagedQuickAccessOrderBy.Name },
                                        { text: t("QuickAccess.Edit.OrderBy.MoreAccess"), value: ManagedQuickAccessOrderBy.MostAccessed },
                                        { text: t("QuickAccess.Edit.OrderBy.LessAccess"), value: ManagedQuickAccessOrderBy.LessAcessed },
                                    ]}
                                    title={t("QuickAccess.Edit.OrderBy.ButtonTooltip")}
                                />
                            </Grid>

                        </Grid>
                    </MprcFilterLine>
                    <Typography variant="caption">{t("QuickAccess.Edit.NewSubscriptionHint")}</Typography>

                    <TextField
                        fullWidth
                        placeholder={t("Search.TextPlaceholder", { ns: "shared" })}
                        value={viewFilter.searchText}
                        onKeyDown={(e) => e.keyCode == 13 && handleReload()}
                        onChange={(e) => handleSearchText(e.target.value, false)}
                    />

                </MprcFilterBox>
            </MprcContainer>

            <MprcContainer>
                {
                    viewData &&
                    <MprcContainer>
                        <MprcFilterCount
                            loading={loading}
                            isEmpty={viewData.length == 0}
                            countText={t("QuickAccess.Edit.RecordsFoundText", { count: viewData.length })}
                            filter={{
                                active: filtered,
                                text: t("QuickAccess.Edit.RecordsFilteredText", { count: viewData.length }),
                                onResetFilter: resetFilters
                            }}
                        />
                    </MprcContainer>
                }

                {
                    viewData && viewData.length > 0 &&
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(auto-fill, 220px)"
                        justifyContent="space-between"
                        gap={2}
                    >
                        {
                            viewData?.map((item, index) => {
                                return (
                                    <RenderCard key={item.subscriptionId} item={item} onToggleHide={setToogleId} />
                                );
                            })
                        }
                    </Box>
                }
            </MprcContainer>
        </>
    );

    return (
        <Container disableGutters maxWidth="sm">

            <MprcRequestErrorReport error={viewError} />

            <MprcRequestIndicator visible={loading || toggleId != null} type={loading ? "request" : "post"} />

            {RenderContent}
        </Container>
    );
};
